import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "../components/ui/drawer";
import DateField from "../components/DateField";
import { Aperture, X, Check, AlertTriangle, Loader2, Minus, Plus, Camera, RefreshCcw } from "lucide-react";

const MAX_DIM = 1024;
const JPEG_QUALITY = 0.75;
const THUMB_DIM = 140;          // small thumbnail stored with the item
const THUMB_QUALITY = 0.7;
const MAX_CONCURRENCY = 5;
const POLL_INTERVAL_MS = 1500;

function captureFromVideo(video) {
  const vw = video.videoWidth || 0;
  const vh = video.videoHeight || 0;
  if (!vw || !vh) return null;
  // Full-size for OCR
  const scale = Math.min(1, MAX_DIM / Math.max(vw, vh));
  const w = Math.round(vw * scale);
  const h = Math.round(vh * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(video, 0, 0, w, h);
  const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  // Tiny thumb for display — square crop centered
  const tCanvas = document.createElement("canvas");
  tCanvas.width = THUMB_DIM;
  tCanvas.height = THUMB_DIM;
  const tCtx = tCanvas.getContext("2d");
  const side = Math.min(vw, vh);
  const sx = (vw - side) / 2;
  const sy = (vh - side) / 2;
  tCtx.drawImage(video, sx, sy, side, side, 0, 0, THUMB_DIM, THUMB_DIM);
  const thumbDataUrl = tCanvas.toDataURL("image/jpeg", THUMB_QUALITY);
  return {
    dataUrl,
    base64: dataUrl.split(",")[1],
    thumbDataUrl,
    thumbBase64: thumbDataUrl.split(",")[1],
  };
}

async function downscaleFile(file) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const out = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  // Thumb (square center crop)
  const tCanvas = document.createElement("canvas");
  tCanvas.width = THUMB_DIM;
  tCanvas.height = THUMB_DIM;
  const tCtx = tCanvas.getContext("2d");
  const side = Math.min(img.width, img.height);
  const sx = (img.width - side) / 2;
  const sy = (img.height - side) / 2;
  tCtx.drawImage(img, sx, sy, side, side, 0, 0, THUMB_DIM, THUMB_DIM);
  const thumbDataUrl = tCanvas.toDataURL("image/jpeg", THUMB_QUALITY);
  return {
    dataUrl: out,
    base64: out.split(",")[1],
    thumbDataUrl,
    thumbBase64: thumbDataUrl.split(",")[1],
  };
}

export default function AddItem() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  // Concurrency limiter for placeholder creation calls (POST /api/scans returns fast)
  const inFlightRef = useRef(0);
  const pendingRef = useRef([]);
  const runNext = useCallback(() => {
    while (inFlightRef.current < MAX_CONCURRENCY && pendingRef.current.length > 0) {
      const task = pendingRef.current.shift();
      inFlightRef.current += 1;
      task().finally(() => {
        inFlightRef.current -= 1;
        runNext();
      });
    }
  }, []);
  const enqueue = useCallback(
    (task) => {
      pendingRef.current.push(task);
      runNext();
    },
    [runNext]
  );

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  // Queue entry shape:
  // { id: local-uuid, thumb, item_id?, status, product_name, expiry_date, quantity, error? }
  // status: 'uploading' | 'processing' | 'success' | 'review' | 'error'
  const [queue, setQueue] = useState([]);
  const [editId, setEditId] = useState(null);
  const [flash, setFlash] = useState(false);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Camera API not available");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        // Wait until video has real dimensions before letting user press shutter
        await new Promise((resolve) => {
          if (v.videoWidth && v.videoHeight) return resolve();
          const onReady = () => { v.removeEventListener("loadedmetadata", onReady); resolve(); };
          v.addEventListener("loadedmetadata", onReady);
        });
        try { await v.play(); } catch (_e) { /* mobile may reject but keep trying */ }
      }
      setCameraReady(true);
    } catch (e) {
      setCameraReady(false);
      setCameraError(e?.message || "Camera unavailable");
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      streamRef.current?.getTracks()?.forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [startCamera]);

  const updateItem = useCallback((id, patch) => {
    setQueue((q) => q.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  // Poll scans/status for all processing items until all resolve.
  // Uses a ref to read latest queue without restarting the interval on every change.
  const queueRef = useRef(queue);
  useEffect(() => { queueRef.current = queue; }, [queue]);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const pendingIds = queueRef.current
        .filter((q) => q.status === "processing" && q.item_id)
        .map((q) => q.item_id);
      if (pendingIds.length === 0) return;
      try {
        const r = await api.get(`/scans/status?ids=${encodeURIComponent(pendingIds.join(","))}`);
        const byId = {};
        (r.data.items || []).forEach((i) => { byId[i.item_id] = i; });
        if (!alive) return;
        setQueue((q) => q.map((it) => {
          if (it.status !== "processing" || !it.item_id) return it;
          const s = byId[it.item_id];
          if (!s) return it;
          // Terminal states: pending_confirm (OCR ok, awaiting user insert) OR active
          if (s.status === "pending_confirm" || s.status === "active") {
            return { ...it, status: "success", product_name: s.product_name, expiry_date: s.expiry_date, quantity: s.quantity };
          }
          if (s.status === "needs_review") {
            return {
              ...it,
              status: "review",
              product_name: s.product_name === "Needs review" ? "" : s.product_name || "",
              expiry_date: s.expiry_date || "",
              quantity: s.quantity || 1,
              error: "Tocca per completare i dati",
            };
          }
          return it;
        }));
      } catch (_e) { /* retry next tick */ }
    };
    const h = setInterval(tick, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(h); };
  }, []);

  const sendScan = useCallback(
    (localId, base64, thumbBase64) => {
      enqueue(async () => {
        try {
          const res = await api.post("/scans", { image_base64: base64, mime_type: "image/jpeg", thumb_base64: thumbBase64 });
          updateItem(localId, { item_id: res.data.item_id, status: "processing" });
        } catch (_e) {
          updateItem(localId, { status: "error", error: "Upload failed — tap to retry" });
        }
      });
    },
    [enqueue, updateItem]
  );

  const doCapture = useCallback(() => {
    if (!videoRef.current || !cameraReady) return;
    const shot = captureFromVideo(videoRef.current);
    if (!shot) return;
    const id = (crypto.randomUUID && crypto.randomUUID()) || `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const entry = { id, thumb: shot.thumbDataUrl || shot.dataUrl, status: "uploading", quantity: 1, product_name: "", expiry_date: "", _base64: shot.base64, _thumb64: shot.thumbBase64 };
    setQueue((q) => [...q, entry]);
    setFlash(true);
    setTimeout(() => setFlash(false), 120);
    try { navigator.vibrate?.(10); } catch (_e) { /* noop */ }
    sendScan(id, shot.base64, shot.thumbBase64);
  }, [cameraReady, sendScan]);

  const retryUpload = useCallback((entry) => {
    if (!entry._base64) return;
    updateItem(entry.id, { status: "uploading", error: null });
    sendScan(entry.id, entry._base64, entry._thumb64);
  }, [sendScan, updateItem]);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const f of files) {
      try {
        const shot = await downscaleFile(f);
        const id = (crypto.randomUUID && crypto.randomUUID()) || `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        setQueue((q) => [...q, { id, thumb: shot.thumbDataUrl || shot.dataUrl, status: "uploading", quantity: 1, product_name: "", expiry_date: "", _base64: shot.base64, _thumb64: shot.thumbBase64 }]);
        sendScan(id, shot.base64, shot.thumbBase64);
      } catch (_err) {
        toast.error("Immagine non leggibile");
      }
    }
    e.target.value = "";
  };

  const editing = queue.find((q) => q.id === editId) || null;

  const commitEdit = async () => {
    if (!editing) return;
    if (!editing.product_name?.trim() || !editing.expiry_date) {
      toast.error("Name and expiry required");
      return;
    }
    if (!editing.item_id) {
      toast.error("Still uploading");
      return;
    }
    try {
      await api.patch(`/items/${editing.item_id}`, {
        product_name: editing.product_name.trim(),
        expiry_date: editing.expiry_date,
        quantity: Math.max(1, Number(editing.quantity) || 1),
      });
      updateItem(editing.id, { status: "success" });
      toast.success("Saved");
    } catch (_e) {
      toast.error("Save failed");
    }
    setEditId(null);
  };

  const dropFromQueue = async (id) => {
    const it = queue.find((q) => q.id === id);
    setQueue((q) => q.filter((x) => x.id !== id));
    if (it?.item_id) {
      try { await api.delete(`/items/${it.item_id}`); } catch (_e) { /* noop */ }
    }
  };

  const savedCount = queue.filter((q) => q.status === "success").length;
  const processingCount = queue.filter((q) => q.status === "processing" || q.status === "uploading").length;
  const reviewCount = queue.filter((q) => q.status === "review").length;

  const done = () => navigate("/dashboard");

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="add-item-page">
      <header className="absolute top-0 left-0 right-0 z-20 pt-safe">
        <div className="flex items-center justify-between px-5 pt-4">
          <button
            onClick={done}
            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center"
            data-testid="close-camera-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="text-center">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/70">Burst scan</div>
            <div className="font-heading text-sm mt-0.5">
              {savedCount} ok · {processingCount} in corso{reviewCount ? ` · ${reviewCount} review` : ""}
            </div>
            {processingCount > 0 && (
              <div className="text-[10px] text-fresh-green mt-1 animate-pulse" data-testid="burst-hint">
                📸 Continua a scattare — elaboro in background
              </div>
            )}
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center"
            data-testid="gallery-btn"
            aria-label="Pick from files"
          >
            <Camera className="w-5 h-5" />
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFile} data-testid="ocr-file-input" />
        </div>
      </header>

      <div className="relative flex-1 overflow-hidden">
        <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" data-testid="camera-video" />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="w-[78%] aspect-[4/5] rounded-3xl border-2 border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
        </div>
        {flash && <div className="absolute inset-0 bg-white animate-fade-in opacity-70" />}
        {!cameraReady && !cameraError && (
          <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Avvio fotocamera…
          </div>
        )}
        {cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
            <AlertTriangle className="w-8 h-8 text-fresh-yellow" />
            <div className="mt-3 font-heading text-xl">Fotocamera bloccata</div>
            <p className="mt-2 text-white/70 text-sm max-w-xs">Concedi il permesso alla fotocamera o carica da galleria.</p>
            <div className="mt-5 flex gap-3">
              <Button onClick={startCamera} className="bg-white text-black rounded-xl" data-testid="retry-camera-btn">
                <RefreshCcw className="w-4 h-4 mr-2" /> Riprova
              </Button>
              <Button onClick={() => fileRef.current?.click()} variant="secondary" className="bg-white/10 text-white rounded-xl">
                Carica immagine
              </Button>
            </div>
          </div>
        )}
      </div>

      {queue.length > 0 && (
        <div className="absolute left-0 right-0 bottom-40 z-10 px-4" data-testid="thumbnail-queue">
          <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
            {queue.map((it) => (
              <QueueThumb
                key={it.id}
                item={it}
                onOpen={() => {
                  if (it.status === "error") { retryUpload(it); return; }
                  setEditId(it.id);
                }}
                onRemove={() => dropFromQueue(it.id)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 z-20 pb-safe pt-6 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-center justify-around px-8 pb-4">
          <div className="w-12" />
          <button
            onClick={doCapture}
            disabled={!cameraReady}
            style={{ touchAction: "manipulation" }}
            className="relative w-20 h-20 rounded-full bg-white disabled:opacity-40 active:scale-90 transition-transform shadow-xl"
            data-testid="shutter-btn"
            aria-label="Capture"
          >
            <span className="absolute inset-2 rounded-full border-4 border-black/10" />
            <Aperture className="w-8 h-8 text-zinc-900 absolute inset-0 m-auto" />
            {queue.length > 0 && (
              <span
                className="absolute -top-2 -right-2 min-w-[28px] h-7 px-1 rounded-full bg-fresh-green text-black text-xs font-bold flex items-center justify-center shadow-md"
                data-testid="shutter-counter"
              >
                {queue.length}
              </span>
            )}
          </button>
          <button
            onClick={done}
            className="px-4 h-12 rounded-full bg-fresh-green text-black font-semibold text-sm flex items-center gap-2 active:scale-95"
            data-testid="done-btn"
          >
            <Check className="w-4 h-4" /> Fatto {savedCount ? `(${savedCount})` : ""}
          </button>
        </div>
      </div>

      <Drawer open={!!editing} onOpenChange={(o) => !o && setEditId(null)}>
        <DrawerContent data-testid="edit-drawer">
          {editing && (
            <div className="mx-auto w-full max-w-md text-zinc-950">
              <DrawerHeader className="text-left">
                <DrawerTitle className="font-heading text-2xl font-medium">
                  {editing.status === "review" ? "Controlla i dati" : "Modifica articolo"}
                </DrawerTitle>
                <DrawerDescription>
                  {editing.error ? editing.error : "Aggiorna e salva."}
                </DrawerDescription>
              </DrawerHeader>
              <div className="px-4 space-y-4">
                <img src={editing.thumb} alt="" className="w-full aspect-[4/3] object-cover rounded-xl" />
                <div>
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Prodotto</label>
                  <Input
                    value={editing.product_name || ""}
                    onChange={(e) => updateItem(editing.id, { product_name: e.target.value })}
                    placeholder="es. Latte fresco"
                    className="mt-1 h-12 rounded-xl bg-zinc-50 border-zinc-200"
                    data-testid="edit-product-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Scadenza</label>
                  <DateField
                    value={editing.expiry_date || ""}
                    onChange={(iso) => updateItem(editing.id, { expiry_date: iso })}
                    testId="edit-expiry-date"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Quantità</label>
                  <div className="mt-1 flex items-center gap-3">
                    <button
                      onClick={() => updateItem(editing.id, { quantity: Math.max(1, (editing.quantity || 1) - 1) })}
                      className="w-12 h-12 rounded-xl bg-zinc-100 flex items-center justify-center active:scale-95"
                      data-testid="edit-qty-minus"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <div className="flex-1 h-12 rounded-xl bg-zinc-50 border border-zinc-200 font-heading text-xl font-semibold flex items-center justify-center" data-testid="edit-qty-value">
                      {editing.quantity || 1}
                    </div>
                    <button
                      onClick={() => updateItem(editing.id, { quantity: (editing.quantity || 1) + 1 })}
                      className="w-12 h-12 rounded-xl bg-zinc-100 flex items-center justify-center active:scale-95"
                      data-testid="edit-qty-plus"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              <DrawerFooter className="flex-row gap-3">
                <Button
                  onClick={() => { dropFromQueue(editing.id); setEditId(null); }}
                  variant="secondary"
                  className="flex-1 h-12 rounded-xl bg-zinc-100 text-zinc-950"
                  data-testid="edit-discard-btn"
                >
                  Scarta
                </Button>
                <Button onClick={commitEdit} className="flex-1 h-12 rounded-xl bg-zinc-950 text-white" data-testid="edit-save-btn">
                  Salva
                </Button>
              </DrawerFooter>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{scrollbar-width:none}`}</style>
    </div>
  );
}

function QueueThumb({ item, onOpen, onRemove }) {
  // Instant success on shutter: uploading/processing show a subtle spinner with a green ring (photo is safe).
  let ring = "ring-2 ring-white/60";
  if (item.status === "success") ring = "ring-2 ring-fresh-green";
  else if (item.status === "review") ring = "ring-2 ring-fresh-yellow";
  else if (item.status === "error") ring = "ring-2 ring-fresh-red";
  else ring = "ring-2 ring-fresh-green"; // uploading/processing: photo safely saved

  return (
    <div className={`relative shrink-0 w-20 h-20 rounded-xl overflow-hidden ${ring}`} data-testid={`queue-item-${item.id}`}>
      <img src={item.thumb} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/30" />
      <button onClick={onOpen} className="absolute inset-0 w-full h-full flex items-center justify-center" data-testid={`queue-open-${item.id}`}>
        {item.status === "uploading" && <Loader2 className="w-6 h-6 text-white animate-spin" />}
        {item.status === "processing" && (
          <span className="w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          </span>
        )}
        {item.status === "success" && (
          <span className="w-8 h-8 rounded-full bg-fresh-green flex items-center justify-center">
            <Check className="w-4 h-4 text-black" strokeWidth={3} />
          </span>
        )}
        {item.status === "review" && (
          <span className="w-8 h-8 rounded-full bg-fresh-yellow flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-black" strokeWidth={2.5} />
          </span>
        )}
        {item.status === "error" && (
          <span className="w-8 h-8 rounded-full bg-fresh-red flex items-center justify-center">
            <RefreshCcw className="w-4 h-4 text-white" strokeWidth={2.5} />
          </span>
        )}
      </button>
      {item.status === "success" && (
        <div className="absolute bottom-0 left-0 right-0 bg-fresh-green/90 text-black text-[9px] font-bold uppercase tracking-widest text-center py-0.5">
          Da inserire
        </div>
      )}
      {item.status === "review" && (
        <div className="absolute bottom-0 left-0 right-0 bg-fresh-yellow/90 text-black text-[9px] font-bold uppercase tracking-widest text-center py-0.5">
          Review
        </div>
      )}
      {(item.status === "error" || item.status === "review") && (
        <button
          onClick={onRemove}
          className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white text-black flex items-center justify-center"
          data-testid={`queue-remove-${item.id}`}
          aria-label="Remove"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
